import { useState } from 'react';
import {
  ArrowLeft, PenLine, ShieldCheck, AlertTriangle, Fingerprint,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import ChipEstado from '../ui/ChipEstado';
import {
  ESTADO_CALIDAD_LOTE, ESTADO_INSPECCION, RESULTADO_INSPECCION,
  RESULTADO_MEDIDA,
} from '../ui/estados';
import { useAuth } from '../lib/contextoAuth';
import CampoContrasena from '../ui/CampoContrasena';

// LA FICHA DE UNA INSPECCIÓN · Fase 3 del módulo de Calidad.
//
// Es la pantalla que un auditor mira por encima del hombro, así que
// enseña las tres cosas que va a pedir sin que haya que buscarlas: qué
// se midió, contra qué, y quién lo firmó.
//
// DOS DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **Firmar explica la consecuencia ANTES** (Doctrina 2, regla 7).
//      Es irreversible: después de firmar no se puede tocar nada. Se
//      dice en una frase, y se vuelve a pedir la clave — que es lo que
//      convierte esto en una firma y no en un clic.
//   2. **Liberar es OTRO botón y OTRO permiso.** Firmar una inspección
//      aprobada no libera el lote solo. A quien no puede liberar no se
//      le enseña el botón; a quien sí, se le ofrece con el motivo ya
//      escrito para que sea un clic.

function Medida({ m }) {
  const rango = m.clase === 'VARIABLE' && m.nominal !== null && m.nominal !== undefined
    ? `${m.nominal}${m.unidad ? ` ${m.unidad}` : ''}`
      + (m.tolerancia_inferior !== null && m.tolerancia_inferior !== undefined
        ? ` (${m.tolerancia_inferior} / +${m.tolerancia_superior ?? 0})` : '')
    : null;

  return (
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-2 last:border-0">
      <div className="min-w-0">
        <div className="font-medium text-gray-900">{m.nombre}</div>
        {rango && <div className="text-xs text-gray-500">Debe medir {rango}</div>}
        {m.instrumento && (
          <div className="text-xs text-gray-500">Medido con {m.instrumento}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-semibold text-gray-900">
          {m.clase === 'ATRIBUTO' ? (m.atributo ? 'Sí' : 'No') : m.valor}
        </div>
        <ChipEstado mapa={RESULTADO_MEDIDA} clave={m.resultado} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Firmar
// ---------------------------------------------------------------------
function Firmar({ inspeccion, onFirmada }) {
  const [abierto, setAbierto] = useState(false);
  const [resultado, setResultado] = useState(inspeccion.resultado);
  const [clave, setClave] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const hayFuera = inspeccion.mediciones.some((m) => m.resultado === 'NOK');

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const d = await api.post(`/inspecciones/${inspeccion.id}/firmar`, {
        resultado, contrasena: clave,
      });
      onFirmada(d);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo firmar.');
    } finally {
      setEnviando(false);
      setClave('');
    }
  }

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
      >
        <PenLine size={16} /> Firmar la inspección
      </button>
    );
  }

  return (
    <form onSubmit={enviar}
          className="border border-amber-300 bg-amber-50 rounded-xl p-4 space-y-3">
      <div className="flex gap-2 text-amber-900">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">
          <strong>Al firmar, esta inspección deja de poderse modificar.</strong>{' '}
          Si después hay que corregir algo, se registra otra inspección que haga
          referencia a ésta y explique el motivo — la de hoy se queda como está.
        </div>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Cómo salió</span>
        <select value={resultado} onChange={(e) => setResultado(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="APROBADO" disabled={hayFuera}>
            Aprobado — todo dentro de tolerancia
            {hayFuera ? ' (no se puede: hay una medida fuera)' : ''}
          </option>
          <option value="CONDICIONAL">
            Con observaciones — pasa, pero hay algo que anotar
          </option>
          <option value="RECHAZADO">Rechazado — no cumple</option>
        </select>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">
          Tu contraseña
        </span>
        <CampoContrasena
          value={clave} onChange={(e) => setClave(e.target.value)}
          autoComplete="current-password"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <span className="block text-xs text-gray-600 mt-1">
          Se pide otra vez aunque hayas entrado hoy: es lo que hace que esta
          firma sea tuya y no de quien encuentre tu sesión abierta.
        </span>
      </label>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          type="submit" disabled={!clave || enviando}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {enviando ? 'Firmando…' : 'Firmar'}
        </button>
        <button type="button" onClick={() => setAbierto(false)}
                className="text-sm text-gray-600 hover:underline">
          Todavía no
        </button>
        {!clave && <span className="text-sm text-gray-500">Falta tu contraseña.</span>}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// Liberar / retener / rechazar el lote
// ---------------------------------------------------------------------
function CambiarEstado({ inspeccion, onCambiado }) {
  const { puede } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState('LIBERADO');
  const [motivo, setMotivo] = useState(
    inspeccion.estado === 'FIRMADA'
      ? `Inspección ${inspeccion.folio} ${
        inspeccion.resultado === 'APROBADO' ? 'aprobada' : `con resultado ${inspeccion.resultado.toLowerCase()}`}.`
      : '');
  const [concesion, setConcesion] = useState(false);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  if (!puede('calidad.liberar')) return null;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const d = await api.post(`/inspecciones/lotes/${inspeccion.id_lote}/estado`, {
        estado_nuevo: estado,
        motivo,
        con_concesion: concesion,
        id_inspeccion: inspeccion.estado === 'FIRMADA' ? inspeccion.id : null,
      });
      onCambiado(d);
      setAbierto(false);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setEnviando(false);
    }
  }

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="inline-flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 text-sm font-semibold text-gray-800"
      >
        <ShieldCheck size={16} /> Decidir qué pasa con el lote
      </button>
    );
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
      <h3 className="font-semibold text-gray-900">
        Qué pasa con el lote {inspeccion.codigo_lote}
      </h3>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Decisión</span>
        <select value={estado} onChange={(e) => setEstado(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="LIBERADO">Liberado — puede salir</option>
          <option value="CONDICIONAL">Liberado con observaciones</option>
          <option value="CUARENTENA">Retenido — que no salga todavía</option>
          <option value="RECHAZADO">Rechazado — no cumple</option>
        </select>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Por qué</span>
        <textarea
          value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <span className="block text-xs text-gray-500 mt-1">
          Dentro de un año, esta frase es lo único que va a explicar esta
          decisión.
        </span>
      </label>

      {puede('calidad.liberar_con_concesion') && estado !== 'RECHAZADO' && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={concesion} className="mt-1"
                 onChange={(e) => setConcesion(e.target.checked)} />
          <span>
            <strong>Sale con algo fuera de especificación (concesión).</strong>
            <span className="block text-xs text-gray-600">
              Márcalo solo si de verdad no cumple y aun así se decide
              embarcarlo. Se cuenta aparte, que es justo lo que un auditor
              cuenta.
            </span>
          </span>
        </label>
      )}

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          type="submit" disabled={motivo.trim().length < 3 || enviando}
          className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {enviando ? 'Guardando…' : 'Guardar la decisión'}
        </button>
        <button type="button" onClick={() => setAbierto(false)}
                className="text-sm text-gray-600 hover:underline">
          Cancelar
        </button>
        {motivo.trim().length < 3 && (
          <span className="text-sm text-gray-500">Falta escribir por qué.</span>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// La ficha
// ---------------------------------------------------------------------
export default function FichaInspeccion({ inspeccion, onVolver, onCambio }) {
  const [aviso, setAviso] = useState(inspeccion.mensaje || null);
  const [datos, setDatos] = useState(inspeccion);

  function refrescar(d) {
    setDatos(d);
    setAviso(d.mensaje || null);
    if (onCambio) onCambio();
  }

  return (
    <div className="space-y-5">
      <button onClick={onVolver}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline">
        <ArrowLeft size={16} /> Volver a las inspecciones
      </button>

      <header className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-gray-900">
            Inspección {datos.folio}
          </h1>
          <ChipEstado mapa={ESTADO_INSPECCION} clave={datos.estado} />
          <ChipEstado mapa={RESULTADO_INSPECCION} clave={datos.resultado} />
        </div>
        <p className="text-sm text-gray-600">
          {datos.tipo_nombre} · {datos.sku} {datos.producto} · lote{' '}
          <strong>{datos.codigo_lote}</strong>
          {datos.ubicacion ? ` · en ${datos.ubicacion}` : ''}
        </p>
        <p className="text-sm text-gray-600">
          El lote está{' '}
          <ChipEstado mapa={ESTADO_CALIDAD_LOTE} clave={datos.estado_calidad_lote} />
        </p>
      </header>

      {aviso && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}

      {datos.id_inspeccion_corrige && (
        <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-3">
          Esta inspección corrige a la número {datos.id_inspeccion_corrige}.{' '}
          <span className="text-gray-600">{datos.motivo_correccion}</span>
        </div>
      )}

      <section className="border border-gray-200 rounded-xl p-4 bg-white">
        <h2 className="font-semibold text-gray-900 mb-2">Qué se midió</h2>
        {datos.mediciones.length === 0 ? (
          <p className="text-sm text-gray-600">
            Sin medidas. Una inspección vacía no se puede
            firmar.
          </p>
        ) : datos.mediciones.map((m) => <Medida key={m.id} m={m} />)}
      </section>

      {datos.estado === 'FIRMADA' && (
        <section className="border border-gray-200 rounded-xl p-4 bg-white space-y-2">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Fingerprint size={18} className="text-gray-400" /> La firma
          </h2>
          <p className="text-sm text-gray-700">
            Firmada por <strong>{datos.firmante}</strong>.
          </p>
          <p className="text-xs text-gray-500 break-all">
            Huella del contenido: {datos.huella}
          </p>
          <p className="text-xs text-gray-500">
            Es la prueba de que lo que se lee hoy es lo que se firmó ese día. Si
            alguien cambiara un solo número, esta huella dejaría de cuadrar.
          </p>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        {datos.estado === 'BORRADOR' && (
          <Firmar inspeccion={datos} onFirmada={refrescar} />
        )}
        <CambiarEstado
          inspeccion={datos}
          onCambiado={(d) => {
            setDatos((a) => ({
              ...a,
              estado_calidad_lote: d.estado_calidad,
              estado_calidad_nombre: d.estado_calidad_nombre,
            }));
            setAviso(d.mensaje);
            if (onCambio) onCambio();
          }}
        />
      </div>
    </div>
  );
}
